import {
  Injector,
  enableProdMode,
  importProvidersFrom,
  provideAppInitializer,
} from '@angular/core';

import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import {
  TranslateCompiler,
  TranslateLoader,
  TranslateModule,
} from '@ngx-translate/core';
import { NgxsDispatchPluginModule } from '@ngxs-labs/dispatch-decorator';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsModule } from '@ngxs/store';
import { OktaAuthModule } from '@okta/okta-angular';
import { OktaAuth, TokenParams } from '@okta/okta-auth-js';
import {
  getInitConfigFactory,
  initAccessibilityObserversFactory,
} from '@sc/data-access-app-injector';
import { AssetsService } from '@sc/data-access-assets';
import { DataAccessEnvironmentModule } from '@sc/data-access-environment';
import {
  CLIENT_APP_ID,
  HIGHLIGHT_REQUIRED_INPUTS,
} from '@sc/data-access-models';
import {
  AppointmentsFilterState,
  AppointmentsState,
  CTAQueryState,
  DossierState,
  DotFittersState,
  FilesState,
  FittersState,
  NavigationState,
  ScoresState,
} from '@sc/data-access-state-management';
import {
  CustomLangLoaderService,
  CustomMatPaginatorIntl,
  LocaleProvider,
  TabTitleStrategyService,
} from '@sc/data-access-translation';
import { FeatureAuthModule } from '@sc/feature-auth-okta';
import {
  SearchDossiersState,
  SearchDossiersStateDefault,
  SearchDossiersStateKey,
} from '@sc/feature-insurer-dossier-search';
import {
  GTagManagerService,
  MatomoService,
  TAG_MANAGER,
} from '@sc/feature-tag-manager';
import { ThemesManagerService } from '@sc/feature-themes-manager';
import {
  RegisterAppB2BService,
  RegisterAppService,
} from '@sc/register-app-actions';
import {
  CUSTOM_DATE_FORMATS,
  DayjsMaterialDateAdapterService,
} from '@sc/ui-components';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { TourMatMenuModule } from 'ngx-ui-tour-md-menu';
import { of } from 'rxjs';
import { AppRoutingModule } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { GraphQLModule } from './app/graphql.module';
import { environment } from './environments/environment';

const oktaAuth = new OktaAuth(environment.auth);

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      BrowserModule,
      AppRoutingModule,
      DataAccessEnvironmentModule.forRoot(environment),
      OktaAuthModule.forRoot({
        oktaAuth,
        // Action activated on OktaAuthGuard fail (redirection to /login)
        onAuthRequired: (
          oktaAuth: OktaAuth,
          injector: Injector,
          options?: TokenParams
        ) => {
          const router = injector.get(Router);
          router.navigate(['login']);
        },
      }),
      FeatureAuthModule.forRoot({
        redirectAfterLogin: '/declaration/customer-data',
        redirectAfterLogout: '',
      }),
      GraphQLModule,
      NgxsStoragePluginModule.forRoot({
        keys: [SearchDossiersState],
        beforeSerialize: (objToStore, key) => {
          if (key === SearchDossiersStateKey) {
            const state = {
              ...SearchDossiersStateDefault,
            };
            if (objToStore) {
              state.dossiersStates = objToStore.dossiersStates;
            }
            return state;
          }
          return objToStore;
        },
      }),
      NgxsModule.forRoot(
        [
          FittersState,
          CTAQueryState,
          NavigationState,
          ScoresState,
          AppointmentsState,
          DossierState,
          AppointmentsFilterState,
          FilesState,
          DotFittersState,
        ],
        {
          developmentMode:
            String(environment.log.logActivationStateManagement) === 'true',
        }
      ),
      NgxsDispatchPluginModule.forRoot(),
      NgxsLoggerPluginModule.forRoot({
        disabled:
          String(environment.log.logActivationStateManagement) !== 'true',
      }),
      NgxsReduxDevtoolsPluginModule.forRoot({
        disabled:
          String(environment.log.logActivationStateManagement) !== 'true',
      }),
      NgxsModule.forFeature([SearchDossiersState]),
      TranslateModule.forRoot({
        compiler: {
          provide: TranslateCompiler,
          useClass: TranslateMessageFormatCompiler,
        },
        loader: { provide: TranslateLoader, useClass: CustomLangLoaderService },
      }),
      TourMatMenuModule,
      MatSnackBarModule,
      MatDialogModule,
      MatBottomSheetModule,
      MatButtonModule
    ),
    LocaleProvider,
    { provide: CLIENT_APP_ID, useValue: 'insurer' },
    { provide: HIGHLIGHT_REQUIRED_INPUTS, useValue: false },
    provideAppInitializer(() => {
      const initializerFn = getInitConfigFactory(function (injector: Injector) {
        // Force init of following services
        injector.get(AssetsService);
        injector.get(ThemesManagerService);
        // Remove tabTitleStrategy after its refacto in angular 14, until then we want it to be eagerly loaded at app init
        injector.get(TabTitleStrategyService);
        return of(null);
      })();
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = initAccessibilityObserversFactory();
      return initializerFn();
    }),
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
    {
      provide: TAG_MANAGER,
      useExisting: GTagManagerService,
      multi: true,
    },
    {
      provide: TAG_MANAGER,
      useExisting: MatomoService,
      multi: true,
    },
    {
      provide: DateAdapter,
      useClass: DayjsMaterialDateAdapterService,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: CUSTOM_DATE_FORMATS,
    },
    {
      provide: RegisterAppService,
      useExisting: RegisterAppB2BService,
    },
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
  ],
}).catch((err) => console.error(err));
